import styled, { css, keyframes, type DefaultTheme } from 'styled-components';

/**
 * Layout primitives for the post-payment onboarding wizard (AIB-6641).
 *
 * Layout (top → bottom):
 *   ┌────────────────────────────────────────────┐
 *   │ Logo                                       │  ← Header (h-16)
 *   ├────────────────────────────────────────────┤
 *   │   ┌──────────────────────────────────┐     │
 *   │   │ ▓▓▓▓░░░░░░░░░░░░░░░░░░░░░░░░░░ │     │  ← Progress (h-1)
 *   │   │ 2 / 4                       Skip │     │  ← Counter + skip
 *   │   └──────────────────────────────────┘     │
 *   │                                            │
 *   │            ┌─────────────────┐             │
 *   │            │   Step content  │             │  ← max-w 32rem
 *   │            │                 │             │
 *   │            │   [ Back ] [Continue]         │
 *   │            └─────────────────┘             │
 *   └────────────────────────────────────────────┘
 *
 * No `<Card>` wrapper — content sits directly on the page background.
 */

const FORM_MAX_WIDTH = '32rem';
/** Header height — kept as a single source of truth so the Customizing
 * screen can compensate for it without the two values drifting. */
export const HEADER_HEIGHT = '64px';

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(4px); }
  to   { opacity: 1; transform: translateY(0);   }
`;

/**
 * Disable motion for users who've asked the OS to reduce it. Applied to
 * any keyframe animation, the bullet spinner, and the progress fill
 * transitions on the Customizing screen.
 */
const reducedMotion = css`
  @media (prefers-reduced-motion: reduce) {
    animation: none !important;
    transition: none !important;
  }
`;

/**
 * State of an item on the "Setting up your workspace" checklist —
 * shared by `CustomizingBullet`, `CustomizingItemLabel`, and the
 * `customizingItemLabelColor` helper. Kept as a single alias so adding
 * a new state (e.g. `'failed'`) only needs one edit.
 */
export type CustomizingItemState = 'pending' | 'active' | 'done';

export const Page = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;

  ${({ theme: { colors } }) => css`
    background: ${colors?.background};
    color: ${colors?.onSurface};
  `}
`;

export const Header = styled.header`
  height: ${HEADER_HEIGHT};
  display: flex;
  align-items: center;

  ${({ theme: { spacing } }) => css`
    padding: 0 ${spacing?.level6};
  `}
`;

export const Logo = styled.img`
  height: 32px;
  width: auto;
  display: block;
`;

export const ProgressArea = styled.div`
  ${({ theme: { spacing } }) => css`
    padding: 0 ${spacing?.level6};
  `}
`;

export const ProgressInner = styled.div`
  position: relative;
  width: 100%;
  max-width: ${FORM_MAX_WIDTH};
  margin: 0 auto;
`;

export const ProgressTrack = styled.div`
  height: 4px;
  width: 100%;
  border-radius: 999px;
  overflow: hidden;

  ${({ theme: { colors } }) => css`
    background: ${colors?.surfaceContainerHighest};
  `}
`;

export const ProgressFill = styled.div<{ $value: number }>`
  height: 100%;
  border-radius: 999px;
  transition: width 500ms cubic-bezier(0.16, 1, 0.3, 1);

  ${({ $value, theme: { colors } }) => css`
    width: ${Math.max(0, Math.min(100, $value))}%;
    background: ${colors?.primary};
  `}

  ${reducedMotion}
`;

export const ProgressMeta = styled.div`
  display: flex;
  align-items: center;

  ${({ theme: { spacing } }) => css`
    margin-top: ${spacing?.level3};
  `}
`;

export const StepCounter = styled.span`
  ${({ theme: { font, colors } }) => css`
    ${font?.labelMedium}
    color: ${colors?.primary};
  `}
`;

export const SkipButton = styled.button`
  position: absolute;
  right: 0;
  top: calc(4px + 8px);
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;

  ${({ theme: { font, colors } }) => css`
    ${font?.labelMedium}
    color: ${colors?.onSurfaceVariant};
    transition: color 150ms ease;

    &:hover,
    &:focus-visible {
      color: ${colors?.onSurface};
      outline: none;
    }
  `}
`;

export const Main = styled.main`
  flex: 1;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  overflow-y: auto;

  ${({ theme: { spacing } }) => css`
    padding: ${spacing?.level9} ${spacing?.level6} ${spacing?.level7};
  `}
`;

export const FormColumn = styled.div`
  width: 100%;
  max-width: ${FORM_MAX_WIDTH};
  animation: ${fadeIn} 240ms ease-out both;
  ${reducedMotion}
`;

// ── Step header (each step renders this once) ────────────────────────────

export const StepTitle = styled.h2`
  ${({ theme: { font, colors, spacing } }) => css`
    ${font?.headlineSmall}
    font-weight: 600;
    color: ${colors?.onSurface};
    margin: 0 0 ${spacing?.level3};
  `}
`;

export const StepSubtitle = styled.p`
  ${({ theme: { font, colors, spacing } }) => css`
    ${font?.bodyMedium}
    color: ${colors?.onSurfaceVariant};
    margin: 0 0 ${spacing?.level8};
  `}
`;

// ── Field grids ─────────────────────────────────────────────────────────

export const FieldStack = styled.div`
  display: flex;
  flex-direction: column;

  ${({ theme: { spacing } }) => css`
    gap: ${spacing?.level5};
  `}
`;

export const TwoColRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;

  ${({ theme: { spacing } }) => css`
    gap: ${spacing?.level4};
  `}
`;

export const SectionStack = styled.div`
  display: flex;
  flex-direction: column;

  ${({ theme: { spacing } }) => css`
    gap: ${spacing?.level7};
  `}
`;

// ── Single field (label + control + error) ──────────────────────────────

export const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;

  ${({ theme: { spacing } }) => css`
    gap: ${spacing?.level3};
  `}
`;

export const FieldLabel = styled.label`
  /* 12px / 500 / muted — smaller than labelMedium (too bold + tall) and
   * larger than labelSmall (too small) for a compact field label. */
  ${({ theme: { colors } }) => css`
    font-size: 12px;
    line-height: 16px;
    font-weight: 500;
    color: ${colors?.onSurfaceVariant};
  `}
`;

export const RequiredMark = styled.span`
  ${({ theme: { colors, spacing } }) => css`
    color: ${colors?.error};
    margin-left: ${spacing?.level1};
  `}
`;

export const TextInput = styled.input`
  width: 100%;
  height: 44px;
  min-height: 44px;
  box-sizing: border-box;
  line-height: 1.2;

  ${({ theme: { colors, spacing, borderRadius, font } }) => css`
    ${font?.bodyMedium}
    color: ${colors?.onSurface};
    background: ${colors?.surfaceContainerLow};
    border: 1px solid ${colors?.outlineVariant};
    border-radius: ${borderRadius?.small};
    padding: 0 ${spacing?.level4};

    &::placeholder {
      color: ${colors?.onSurfaceVariant};
      opacity: 0.6;
    }

    &:hover {
      border-color: ${colors?.outline};
    }

    &:focus {
      outline: none;
      border-color: ${colors?.primary};
    }
  `}
`;

/**
 * Compact dropdown trigger — same visual footprint as `TextInput` (44px,
 * tight padding, surfaceContainerLow fill) but rendered as a button so we
 * can pair it with our `DropDownMenuPortal` instead of the native
 * `<select>` (which renders the host OS's chrome and ignores our theme).
 */
export const SelectTrigger = styled.button<{ $open: boolean; $empty: boolean }>`
  width: 100%;
  height: 44px;
  min-height: 44px;
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  text-align: left;
  line-height: 1.2;

  ${({ $open, $empty, theme: { colors, spacing, borderRadius, font } }) => css`
    ${font?.bodyMedium}
    color: ${$empty ? colors?.onSurfaceVariant : colors?.onSurface};
    opacity: ${$empty ? 0.6 : 1};
    background: ${colors?.surfaceContainerLow};
    border: 1px solid ${$open ? colors?.primary : colors?.outlineVariant};
    border-radius: ${borderRadius?.small};
    padding: 0 ${spacing?.level4};
    gap: ${spacing?.level3};
    transition:
      border-color 120ms ease,
      background 120ms ease;

    &:hover:not(:disabled) {
      border-color: ${$open ? colors?.primary : colors?.outline};
    }

    &:focus-visible {
      outline: none;
      border-color: ${colors?.primary};
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  `}
`;

export const SelectTriggerLabel = styled.span`
  flex: 1;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

export const SelectTriggerIcon = styled.span<{ $open: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  transition: transform 150ms ease;

  ${({ $open, theme: { colors } }) => css`
    color: ${colors?.onSurfaceVariant};
    transform: rotate(${$open ? 180 : 0}deg);
  `}

  > svg {
    width: 16px;
    height: 16px;
    fill: currentColor;
  }
`;

export const InlineError = styled.div`
  ${({ theme: { font, colors } }) => css`
    ${font?.bodySmall}
    color: ${colors?.error};
  `}
`;

export const ErrorBanner = styled.div`
  ${({ theme: { font, colors, spacing, borderRadius } }) => css`
    ${font?.bodyMedium}
    color: ${colors?.onErrorContainer};
    background: ${colors?.errorContainer};
    border-radius: ${borderRadius?.small};
    padding: ${spacing?.level3} ${spacing?.level4};
    margin-top: ${spacing?.level5};
  `}
`;

// ── Footer (Back / Continue) ────────────────────────────────────────────

export const ActionRow = styled.div`
  display: flex;

  ${({ theme: { spacing } }) => css`
    gap: ${spacing?.level3};
    margin-top: ${spacing?.level7};
    padding-bottom: ${spacing?.level7};
  `}
`;

const pillButtonBase = css`
  flex: 1;
  height: 48px;
  border-radius: 999px;
  cursor: pointer;
  transition:
    background 150ms ease,
    border-color 150ms ease,
    color 150ms ease,
    opacity 150ms ease;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const PrimaryButton = styled.button`
  ${pillButtonBase}

  ${({ theme: { font, colors } }) => css`
    ${font?.labelLarge}
    background: ${colors?.primary};
    color: ${colors?.onPrimary};
    border: none;
    font-weight: 600;

    &:hover:not(:disabled),
    &:focus-visible:not(:disabled) {
      background: ${colors?.primary};
      filter: brightness(0.92);
      outline: none;
    }
  `}
`;

export const SecondaryButton = styled.button`
  ${pillButtonBase}

  ${({ theme: { font, colors } }) => css`
    ${font?.labelLarge}
    background: transparent;
    color: ${colors?.onSurface};
    border: 1px solid ${colors?.outlineVariant};

    &:hover:not(:disabled),
    &:focus-visible:not(:disabled) {
      border-color: ${colors?.outline};
      background: ${colors?.surfaceContainerLow};
      outline: none;
    }
  `}
`;

// ── Skip dialog ─────────────────────────────────────────────────────────
//
// Intentionally compact — title at 20px / body at 14px with pill-shaped
// buttons. We don't reuse `ConfirmDialog` here because it pipes the title
// through `NavigationHeader` at titleLarge (22px) — too big.

export const SkipDialogBody = styled.div`
  /*
   * The Dialog molecule already renders the close-X inside a NavigationHeader
   * at the top, so the body only needs side + bottom padding. We use level7
   * (32px) horizontally so the narrow 28rem panel doesn't feel cramped
   * against the rounded corners.
   */
  ${({ theme: { spacing } }) => css`
    padding: 0 ${spacing?.level7} ${spacing?.level7};
    display: flex;
    flex-direction: column;
    gap: ${spacing?.level5};
  `}
`;

export const SkipDialogTitle = styled.h2`
  /* 20px / font-semibold — matches the dialog's compact visual weight. */
  ${({ theme: { colors } }) => css`
    font-size: 20px;
    line-height: 28px;
    font-weight: 600;
    color: ${colors?.onSurface};
    margin: 0;
  `}
`;

export const SkipDialogDescription = styled.p`
  /* 14px regular — bodyMedium weight, muted colour. */
  ${({ theme: { font, colors } }) => css`
    ${font?.bodyMedium}
    color: ${colors?.onSurfaceVariant};
    margin: 0;
  `}
`;

export const SkipDialogActions = styled.div`
  display: flex;
  justify-content: flex-end;

  ${({ theme: { spacing } }) => css`
    gap: ${spacing?.level3};
    margin-top: ${spacing?.level3};
  `}
`;

const skipPillBase = css`
  height: 40px;
  padding: 0 20px;
  border-radius: 999px;
  cursor: pointer;
  transition:
    background 150ms ease,
    border-color 150ms ease,
    color 150ms ease;

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

export const SkipDialogPrimary = styled.button`
  ${skipPillBase}

  ${({ theme: { font, colors } }) => css`
    ${font?.labelLarge}
    background: ${colors?.primary};
    color: ${colors?.onPrimary};
    border: none;
    font-weight: 600;

    &:hover:not(:disabled),
    &:focus-visible:not(:disabled) {
      filter: brightness(0.92);
      outline: none;
    }
  `}
`;

export const SkipDialogSecondary = styled.button`
  ${skipPillBase}

  ${({ theme: { font, colors } }) => css`
    ${font?.labelLarge}
    background: transparent;
    color: ${colors?.onSurfaceVariant};
    border: 1px solid ${colors?.outlineVariant};

    &:hover:not(:disabled),
    &:focus-visible:not(:disabled) {
      border-color: ${colors?.outline};
      color: ${colors?.onSurface};
      outline: none;
    }
  `}
`;

// ── "Setting up your workspace" screen (post-Finish loader) ──────────────
//
// Post-finish loader: a thin top progress bar, a centred max-w-md column
// with a title/subtitle, and a card containing a 3-item checklist that
// animates from spinner → check, plus a rotating "insight" line below a
// hairline divider. Once the API call resolves *and* the animation
// completes, we briefly flip to "Workspace ready" before handing off to
// the authenticated app.

const spin = keyframes`
  to { transform: rotate(360deg); }
`;

export const CustomizingTopProgress = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  z-index: 10;

  ${({ theme: { colors } }) => css`
    background: ${colors?.surfaceContainerHighest};
  `}
`;

export const CustomizingTopProgressFill = styled.div<{ $value: number }>`
  height: 100%;
  transition: width 100ms linear;

  ${({ $value, theme: { colors } }) => css`
    width: ${Math.max(0, Math.min(100, $value))}%;
    background: ${colors?.primary};
  `}

  ${reducedMotion}
`;

export const CustomizingMain = styled.main`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;

  ${({ theme: { spacing } }) => css`
    /* The header above pushes the geometric centre of <main> down, so the
     * column lands visually below the viewport's mid-line. Pull the column
     * back up by the header's height so it reads as truly centred. */
    margin-top: calc(-1 * ${HEADER_HEIGHT});
    padding: ${spacing?.level9} ${spacing?.level6};
  `}
`;

export const CustomizingColumn = styled.div`
  width: 100%;
  max-width: 28rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  animation: ${fadeIn} 240ms ease-out both;
  ${reducedMotion}
`;

export const CustomizingTitle = styled.h1`
  ${({ theme: { font, colors, spacing } }) => css`
    ${font?.headlineSmall}
    font-weight: 600;
    letter-spacing: -0.01em;
    color: ${colors?.onSurface};
    margin: 0 0 ${spacing?.level3};
  `}
`;

export const CustomizingSubtitle = styled.p`
  ${({ theme: { font, colors, spacing } }) => css`
    ${font?.bodyMedium}
    color: ${colors?.onSurfaceVariant};
    margin: 0 0 ${spacing?.level8};
  `}
`;

export const CustomizingCard = styled.div`
  width: 100%;
  text-align: left;
  backdrop-filter: blur(4px);

  ${({ theme: { colors, borderRadius, spacing } }) => css`
    background: ${colors?.surfaceContainerLow};
    border: 1px solid ${colors?.outlineVariant};
    border-radius: ${borderRadius?.large};
    padding: ${spacing?.level7};
    box-shadow: 0 8px 32px -12px rgba(0, 0, 0, 0.4);
  `}
`;

export const CustomizingList = styled.ul`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;

  ${({ theme: { spacing } }) => css`
    gap: ${spacing?.level5};
  `}
`;

export const CustomizingListItem = styled.li`
  display: flex;
  align-items: center;

  ${({ theme: { spacing } }) => css`
    gap: ${spacing?.level3};
  `}
`;

export const CustomizingBullet = styled.div<{
  $state: CustomizingItemState;
}>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition:
    background 200ms ease,
    color 200ms ease;

  ${({ $state, theme: { colors } }) => {
    if ($state === 'done') {
      return css`
        background: ${colors?.primary};
        color: ${colors?.onPrimary};
      `;
    }
    if ($state === 'active') {
      return css`
        background: transparent;
        color: ${colors?.primary};
      `;
    }
    return css`
      background: transparent;
      color: ${colors?.onSurfaceVariant};
      opacity: 0.4;
    `;
  }}
`;

export const CustomizingBulletDot = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: currentColor;
`;

export const CustomizingBulletSpinner = styled.div`
  width: 14px;
  height: 14px;
  border: 2px solid currentColor;
  border-top-color: transparent;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
  ${reducedMotion}
`;

export const CustomizingBulletCheck = styled.svg.attrs({
  viewBox: '0 0 12 12',
  'aria-hidden': true,
})`
  width: 10px;
  height: 10px;
  stroke: currentColor;
  stroke-width: 2.5;
  stroke-linecap: round;
  stroke-linejoin: round;
  fill: none;
`;

function customizingItemLabelColor(
  state: CustomizingItemState,
  colors: DefaultTheme['colors'] | undefined,
): string | undefined {
  if (state === 'active') return colors?.primary;
  if (state === 'done') return colors?.onSurface;
  return colors?.onSurfaceVariant;
}

export const CustomizingItemLabel = styled.span<{
  $state: CustomizingItemState;
}>`
  ${({ $state, theme: { font, colors } }) => css`
    ${font?.bodyMedium}
    transition: color 200ms ease;
    color: ${customizingItemLabelColor($state, colors)};
    opacity: ${$state === 'pending' ? 0.5 : 1};
    font-weight: ${$state === 'active' ? 500 : 400};
  `}
`;

export const CustomizingInsightArea = styled.div`
  height: 24px; /* prevents layout jump as text rotates */

  ${({ theme: { spacing, colors } }) => css`
    margin-top: ${spacing?.level5};
    padding-top: ${spacing?.level4};
    border-top: 1px solid ${colors?.outlineVariant};
  `}
`;

export const CustomizingInsight = styled.div`
  animation: ${fadeIn} 240ms ease-out both;

  ${({ theme: { font, colors } }) => css`
    ${font?.bodyMedium}
    color: ${colors?.onSurface};
  `}

  ${reducedMotion}
`;
